import {resolveCatalogEntry, type PermissionCatalogEntry} from './permissionGrouping'
import type {RolePermissionRecord} from './rolePermissionRequests'
import {isGrantablePermissionCode} from './rolePermissionGrantability'
import {
  canonicalizeRolePermissionCode,
  isHiddenRolePermissionMatrixAlias,
  normalizeRolePermissionCode,
} from './rolePermissionCanonicalization'

const normalizeCode = (value: string): string => normalizeRolePermissionCode(value)

export function isRolePermissionSelected(row: RolePermissionRecord): boolean {
  const r = row as any
  if (typeof r.selected === 'boolean') return r.selected
  if (typeof r.effectiveAllowed === 'boolean') return r.effectiveAllowed
  const source = (r.source || '').trim().toUpperCase()
  if (source === 'COMPANY_DENY' || source === 'NONE') return false
  return Boolean(row.permission?.name)
}

export function collectSelectedPermissionCodesForRole(
  roleId: number | null,
  rows: RolePermissionRecord[]
): Set<string> {
  if (roleId == null) return new Set()
  const selected = new Set<string>()
  rows.forEach((row) => {
    if (Number(row.roleId) !== roleId || !isRolePermissionSelected(row)) return
    const name = row.permission?.name
    if (!name) return
    const normalized = normalizeCode(name)
    if (isHiddenRolePermissionMatrixAlias(normalized)) {
      selected.add(canonicalizeRolePermissionCode(normalized))
      return
    }
    selected.add(canonicalizeRolePermissionCode(normalized))
  })
  return selected
}

export function buildSelectedPermissionCountByRole(
  rows: RolePermissionRecord[]
): Map<number, number> {
  const counts = new Map<number, number>()
  rows.forEach((row) => {
    const roleId = Number(row.roleId)
    if (!Number.isFinite(roleId) || !isRolePermissionSelected(row)) return
    counts.set(roleId, (counts.get(roleId) ?? 0) + 1)
  })
  return counts
}

export function buildPermissionIdByCode(
  rows: RolePermissionRecord[],
  catalog: PermissionCatalogEntry[]
): Map<string, number> {
  const byCode = new Map<string, number>()

  rows.forEach((row) => {
    const code = row.permission?.name
    const permissionId = row.permission?.id ?? row.permissionId
    if (!code || permissionId == null || !Number.isFinite(Number(permissionId))) return
    const normalized = normalizeCode(code)
    if (isHiddenRolePermissionMatrixAlias(normalized)) {
      return
    }
    byCode.set(canonicalizeRolePermissionCode(normalized), Number(permissionId))
  })

  catalog.forEach((entry) => {
    const resolved = resolveCatalogEntry(entry)
    const permissionId = Number(resolved.permissionId)
    if (!resolved.permissionCode || !Number.isFinite(permissionId)) return
    const normalized = normalizeCode(resolved.permissionCode)
    if (isHiddenRolePermissionMatrixAlias(normalized)) {
      return
    }
    byCode.set(canonicalizeRolePermissionCode(normalized), permissionId)
  })

  return byCode
}

export function filterGrantablePermissionCodes(
  draftCodes: Iterable<string>,
  grantableByCode: Map<string, boolean>,
  actorIsSuperAdmin: boolean
): {grantableCodes: string[]; removedStaleCodes: string[]} {
  const grantableCodes: string[] = []
  const removedStaleCodes: string[] = []
  const codes = Array.from(draftCodes)
  for (const raw of codes) {
    const code = canonicalizeRolePermissionCode(normalizeCode(raw))
    if (!code) continue
    if (isGrantablePermissionCode(code, grantableByCode, actorIsSuperAdmin)) {
      grantableCodes.push(code)
    } else {
      removedStaleCodes.push(code)
    }
  }
  return {grantableCodes, removedStaleCodes}
}

export function collectReplacementPermissionPayload(
  draftCodes: Iterable<string>,
  permissionIdByCode: Map<string, number>,
  grantableByCode?: Map<string, boolean>,
  actorIsSuperAdmin = false
): {permissionIds?: number[]; permissionNames?: string[]} {
  const sourceCodes = grantableByCode
    ? filterGrantablePermissionCodes(draftCodes, grantableByCode, actorIsSuperAdmin).grantableCodes
    : Array.from(draftCodes)
        .map((code) => canonicalizeRolePermissionCode(normalizeCode(code)))
        .filter(Boolean)
  const orderedCodes = Array.from(new Set(sourceCodes))
  const permissionIds = orderedCodes
    .map((code) => permissionIdByCode.get(code))
    .filter((id): id is number => Number.isFinite(id as number))

  if (permissionIds.length === orderedCodes.length) {
    return {permissionIds}
  }

  return {permissionNames: orderedCodes}
}
